/**
 * HomeStateDot — animated status indicator for document state.
 *
 * Visual feedback for doc lifecycle: drafting (pulse hot), review (solid brand),
 * indexing (solid warn), done (solid cool). Follows spec §4.3 & §7.
 *
 * Uses Griffel makeStyles for animation, matching TabBar.activeDot and
 * LoomSpine pulse conventions (no global @keyframes CSS).
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { brandColors, statusColors, motion } from "../tokens";
import type { DocState } from "../homeSceneMapping";

interface HomeStateDotProps {
  state: DocState;
}

const useStyles = makeStyles({
  dot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pulsing: {
    animationName: {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.35 },
    },
    animationDuration: `${motion.pulseDurationMs}ms`,
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      opacity: 1,
    },
  },
});

export function HomeStateDot({ state }: HomeStateDotProps): React.ReactElement {
  const styles = useStyles();

  const colorMap: Record<DocState, string> = {
    drafting: statusColors.hot,
    review: brandColors.mid,
    indexing: statusColors.warn,
    done: statusColors.cool,
  };

  const isPulsing = state === "drafting";

  return (
    <div
      className={`${styles.dot}${isPulsing ? ` ${styles.pulsing}` : ""}`}
      style={{
        backgroundColor: colorMap[state],
      }}
      aria-hidden="true"
    />
  );
}
